import { useState, useEffect, useMemo } from "react";
import Model, { Providers, helper } from "@tripian/model";

type BaseBookableItem =
  | Providers.Gyg.Tour
  | Providers.Bb.Product
  | Providers.Viator.Product
  | Providers.Rezdy.Product
  | Providers.Toristy.Product
  | Providers.Victory.Event
  | Providers.OccasionGenius.Event
  | Providers.Livn.Product;

type BookableItem = BaseBookableItem & { provider: Model.PROVIDER_ID };

interface Catalog {
  title: string;
  items: BookableItem[];
}

const initialCatalog: Catalog[] = [
  { title: "Adventure", items: [] },
  { title: "Food", items: [] },
  { title: "Culture History", items: [] },
  { title: "Sightseeing", items: [] },
  { title: "Art Museum", items: [] },
  { title: "Local Neighborhood", items: [] },
  { title: "Events", items: [] },
  { title: "Others", items: [] },
];

interface PriorityProducts {
  viator?: Providers.Viator.Product[];
  gyg?: Providers.Gyg.Tour[];
  toristy?: Providers.Toristy.Product[];
  victory?: Providers.Victory.Event[];
  bookbarbados?: Providers.Bb.Product[];
  rezdy?: Providers.Rezdy.Product[];
}

interface UseBookableCatalogsProps {
  gygToursCatalog?: Providers.Gyg.CatalogGroup[];
  bbToursCatalog?: Providers.Bb.CatalogGroup[];
  viatorToursCatalog?: Providers.Viator.CatalogGroup[];
  toristyToursCatalog?: Providers.Toristy.CatalogGroup[];
  rezdyProductCatalogGroup?: Providers.Rezdy.CatalogGroup[];
  victoryEventsCatalog?: Providers.Victory.CatalogGroup[];
  occasionGeniusCatalog?: Providers.OccasionGenius.CatalogGroup[];
  livnToursCatalog?: Providers.Livn.CatalogGroup[];
  priorityProducts?: PriorityProducts;
  loadingBbTourCatalog?: boolean;
  gygLoaders?: { tourCatalogLoader: boolean };
  loadingViatorTourCatalog?: boolean;
  loadingToristyTourCatalog?: boolean;
  loadingRezdyTours?: boolean;
  loadingVictoryEventCatalog?: boolean;
  loadingOccasionGeniusCatalog?: boolean;
  loadingLivnTourCatalog?: boolean;
  loadingPriorityTours?: boolean;
}

export const useBookableCatalogs = ({
  gygToursCatalog,
  bbToursCatalog,
  viatorToursCatalog,
  toristyToursCatalog,
  rezdyProductCatalogGroup,
  victoryEventsCatalog,
  occasionGeniusCatalog,
  livnToursCatalog,
  priorityProducts,
  loadingBbTourCatalog,
  gygLoaders,
  loadingViatorTourCatalog,
  loadingToristyTourCatalog,
  loadingRezdyTours,
  loadingVictoryEventCatalog,
  loadingOccasionGeniusCatalog,
  loadingLivnTourCatalog,
  loadingPriorityTours,
}: UseBookableCatalogsProps) => {
  const [catalogs, setCatalogs] = useState<Catalog[]>(
    helper.deepCopy(initialCatalog)
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const enabledProviders = window.tconfig.TOUR_TICKET_PROVIDER_IDS;
    const providerLoadingStates = [
      { id: Model.PROVIDER_ID.BOOK_BARBADOS, loading: loadingBbTourCatalog },
      { id: Model.PROVIDER_ID.GYG, loading: gygLoaders?.tourCatalogLoader },
      { id: Model.PROVIDER_ID.VIATOR, loading: loadingViatorTourCatalog },
      { id: Model.PROVIDER_ID.TORISTY, loading: loadingToristyTourCatalog },
      { id: Model.PROVIDER_ID.REZDY, loading: loadingRezdyTours },
      { id: Model.PROVIDER_ID.VICTORY, loading: loadingVictoryEventCatalog },
      { id: Model.PROVIDER_ID.OCCASION_GENIUS, loading: loadingOccasionGeniusCatalog },
      { id: Model.PROVIDER_ID.LIVN, loading: loadingLivnTourCatalog },
    ];

    const activeProviderLoadings = providerLoadingStates.filter(p =>
      enabledProviders.includes(p.id)
    );
    const isAnyProviderLoading =
      activeProviderLoadings.some(p => p.loading) || loadingPriorityTours;

    const hasPriorityData =
      (priorityProducts?.viator && priorityProducts.viator.length > 0) ||
      (priorityProducts?.gyg && priorityProducts.gyg.length > 0) ||
      (priorityProducts?.toristy && priorityProducts.toristy.length > 0) ||
      (priorityProducts?.victory && priorityProducts.victory.length > 0);

    const hasAnyData =
      hasPriorityData ||
      (gygToursCatalog &&
        gygToursCatalog.some(cg => cg.items && cg.items.length > 0)) ||
      (bbToursCatalog &&
        bbToursCatalog.some(cg => cg.items && cg.items.length > 0)) ||
      (viatorToursCatalog &&
        viatorToursCatalog.some(cg => cg.items && cg.items.length > 0)) ||
      (toristyToursCatalog &&
        toristyToursCatalog.some(cg => cg.items && cg.items.length > 0)) ||
      (rezdyProductCatalogGroup &&
        rezdyProductCatalogGroup.some(cg => cg.items && cg.items.length > 0)) ||
      (victoryEventsCatalog &&
        victoryEventsCatalog.some(cg => cg.items && cg.items.length > 0)) ||
      (occasionGeniusCatalog &&
        occasionGeniusCatalog.some(cg => cg.items && cg.items.length > 0)) ||
      (livnToursCatalog &&
        livnToursCatalog.some(cg => cg.items && cg.items.length > 0));

    if (hasAnyData) {
      setIsLoading(false);
      return;
    }

    if (isAnyProviderLoading) {
      setIsLoading(true);
      return;
    }

    const allProvidersFinished = activeProviderLoadings.every(p => !p.loading);
    if (allProvidersFinished) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
  }, [
    loadingBbTourCatalog,
    gygLoaders?.tourCatalogLoader,
    loadingViatorTourCatalog,
    loadingToristyTourCatalog,
    loadingRezdyTours,
    loadingVictoryEventCatalog,
    loadingOccasionGeniusCatalog,
    loadingLivnTourCatalog,
    loadingPriorityTours,
    gygToursCatalog,
    bbToursCatalog,
    viatorToursCatalog,
    toristyToursCatalog,
    rezdyProductCatalogGroup,
    victoryEventsCatalog,
    occasionGeniusCatalog,
    livnToursCatalog,
    priorityProducts,
    isLoading,
  ]);

  // GYG Tours
  useEffect(() => {
    if (
      gygToursCatalog &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(Model.PROVIDER_ID.GYG)
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        gygToursCatalog.forEach(cg => {
          const idx = newCatalogs.findIndex(c => {
            const normalizedA = (c.title ?? "").trim().toLowerCase();
            const normalizedB = (cg.title ?? "").trim().toLowerCase();
            return normalizedA === normalizedB;
          });

          const itemsWithProvider = cg.items.map(item => ({
            ...item,
            provider: Model.PROVIDER_ID.GYG,
          }));
          if (idx !== -1) {
            newCatalogs[idx].items = [
              ...newCatalogs[idx].items,
              ...itemsWithProvider,
            ];
          } else {
            const others = newCatalogs.find(c => c.title === "Others");
            if (others) {
              others.items.push(...itemsWithProvider);
            }
          }
        });
        return newCatalogs;
      });
    }
  }, [gygToursCatalog]);

  // BB Tours
  useEffect(() => {
    if (
      bbToursCatalog &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(
        Model.PROVIDER_ID.BOOK_BARBADOS
      )
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        bbToursCatalog.forEach(cg => {
          const idx = newCatalogs.findIndex(c => {
            const normalizedA = (c.title ?? "").trim().toLowerCase();
            const normalizedB = (cg.title ?? "").trim().toLowerCase();
            return normalizedA === normalizedB;
          });
          const itemsWithProvider = cg.items.map(item => ({
            ...item,
            provider: Model.PROVIDER_ID.BOOK_BARBADOS,
          }));
          if (idx !== -1) {
            newCatalogs[idx].items = [
              ...newCatalogs[idx].items,
              ...itemsWithProvider,
            ];
          } else {
            const others = newCatalogs.find(c => c.title === "Others");
            if (others) {
              others.items.push(...itemsWithProvider);
            }
          }
        });
        return newCatalogs;
      });
    }
  }, [bbToursCatalog]);

  // Viator Tours
  useEffect(() => {
    if (
      viatorToursCatalog &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(Model.PROVIDER_ID.VIATOR)
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        viatorToursCatalog.forEach(cg => {
          const idx = newCatalogs.findIndex(c => {
            const normalizedA = (c.title ?? "").trim().toLowerCase();
            const normalizedB = (cg.title ?? "").trim().toLowerCase();
            return normalizedA === normalizedB;
          });
          const itemsWithProvider = cg.items.map(item => ({
            ...item,
            provider: Model.PROVIDER_ID.VIATOR,
          }));
          if (idx !== -1) {
            newCatalogs[idx].items = [
              ...newCatalogs[idx].items,
              ...itemsWithProvider,
            ];
          } else {
            const others = newCatalogs.find(c => c.title === "Others");
            if (others) {
              others.items.push(...itemsWithProvider);
            }
          }
        });
        return newCatalogs;
      });
    }
  }, [viatorToursCatalog]);

  // Rezdy Tours
  useEffect(() => {
    if (
      rezdyProductCatalogGroup &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(Model.PROVIDER_ID.REZDY)
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        rezdyProductCatalogGroup.forEach(cg => {
          const idx = newCatalogs.findIndex(c => {
            const normalizedA = (c.title ?? "").trim().toLowerCase();
            const normalizedB = (cg.title ?? "").trim().toLowerCase();
            return normalizedA === normalizedB;
          });
          const itemsWithProvider = cg.items.map(item => ({
            ...item,
            provider: Model.PROVIDER_ID.REZDY,
          }));
          if (idx !== -1) {
            newCatalogs[idx].items = [
              ...newCatalogs[idx].items,
              ...itemsWithProvider,
            ];
          } else {
            const others = newCatalogs.find(c => c.title === "Others");
            if (others) {
              others.items.push(...itemsWithProvider);
            }
          }
        });
        return newCatalogs;
      });
    }
  }, [rezdyProductCatalogGroup]);

  // Toristy Tours
  useEffect(() => {
    if (
      toristyToursCatalog &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(
        Model.PROVIDER_ID.TORISTY
      )
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        toristyToursCatalog.forEach(cg => {
          const idx = newCatalogs.findIndex(c => {
            const normalizedA = (c.title ?? "").trim().toLowerCase();
            const normalizedB = (cg.title ?? "").trim().toLowerCase();
            return normalizedA === normalizedB;
          });
          const itemsWithProvider = cg.items.map(item => ({
            ...item,
            provider: Model.PROVIDER_ID.TORISTY,
          }));
          if (idx !== -1) {
            newCatalogs[idx].items = [
              ...newCatalogs[idx].items,
              ...itemsWithProvider,
            ];
          } else {
            const others = newCatalogs.find(c => c.title === "Others");
            if (others) {
              others.items.push(...itemsWithProvider);
            }
          }
        });
        return newCatalogs;
      });
    }
  }, [toristyToursCatalog]);

  // Victory Events
  useEffect(() => {
    if (
      victoryEventsCatalog &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(
        Model.PROVIDER_ID.VICTORY
      )
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        const eventsCategory = newCatalogs.find(c => c.title === "Events");
        if (eventsCategory) {
          victoryEventsCatalog.forEach(cg => {
            if (Array.isArray(cg.items)) {
              const itemsWithProvider = cg.items.map(item => ({
                ...item,
                provider: Model.PROVIDER_ID.VICTORY,
              }));
              eventsCategory.items = [
                ...eventsCategory.items,
                ...itemsWithProvider,
              ];
            }
          });
        }
        return newCatalogs;
      });
    }
  }, [victoryEventsCatalog]);

  // OccasionGenius Events
  useEffect(() => {
    if (
      occasionGeniusCatalog &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(
        Model.PROVIDER_ID.OCCASION_GENIUS
      )
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        const eventsCategory = newCatalogs.find(c => c.title === "Events");
        if (eventsCategory) {
          occasionGeniusCatalog.forEach(cg => {
            if (Array.isArray(cg.items)) {
              const itemsWithProvider = cg.items.map(item => ({
                ...item,
                provider: Model.PROVIDER_ID.OCCASION_GENIUS,
              }));
              eventsCategory.items = [
                ...eventsCategory.items,
                ...itemsWithProvider,
              ];
            }
          });
        }
        return newCatalogs;
      });
    }
  }, [occasionGeniusCatalog]);

  // Livn Tours
  useEffect(() => {
    if (
      livnToursCatalog &&
      window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(Model.PROVIDER_ID.LIVN)
    ) {
      setCatalogs(prevCatalogs => {
        const newCatalogs = [...prevCatalogs];
        livnToursCatalog.forEach(cg => {
          const idx = newCatalogs.findIndex(c => {
            const normalizedA = (c.title ?? "").trim().toLowerCase();
            const normalizedB = (cg.title ?? "").trim().toLowerCase();
            return normalizedA === normalizedB;
          });
          const itemsWithProvider = cg.items.map(item => ({
            ...item,
            provider: Model.PROVIDER_ID.LIVN,
          }));
          if (idx !== -1) {
            newCatalogs[idx].items = [
              ...newCatalogs[idx].items,
              ...itemsWithProvider,
            ];
          } else {
            const others = newCatalogs.find(c => c.title === "Others");
            if (others) others.items.push(...itemsWithProvider);
          }
        });
        return newCatalogs;
      });
    }
  }, [livnToursCatalog]);

  // Priority products are now returned separately, not mixed with catalog

  // Sort catalogs and items
  useEffect(() => {
    setCatalogs(prevCatalogs => {
      const newCatalogs = [...prevCatalogs];
      // Sort catalogs by item count
      newCatalogs.sort((a, b) => b.items.length - a.items.length);

      // Sort items by price
      newCatalogs.forEach(c => {
        c.items.sort((a, b) => {
          // Victory Events için price sorting yapmıyoruz
          if ("id" in a || "id" in b) return 0;

          let aPrice = 0;
          const aGygTour = a as Providers.Gyg.Tour;
          const aBbProduct = a as Providers.Bb.Product;
          const aViProduct = a as Providers.Viator.Product;
          const aRezdyProduct = a as Providers.Rezdy.Product;
          const aToristyProduct = a as unknown as Providers.Toristy.Product;

          // Gyg
          if (aGygTour.tour_id) {
            aPrice = aGygTour.price ? aGygTour.price.values.amount : 0;
            // Bb
          } else if (aBbProduct.info) {
            let amount = aBbProduct.offers[0].priceBreakDowns.find(
              pb => pb.touristType === "ADULT"
            )?.price.amount;
            if (amount === undefined) {
              amount = aBbProduct.offers[0].priceBreakDowns[0].price.amount;
            }
            aPrice = amount ?? 0;
            // Viator
          } else if (aViProduct.translationInfo) {
            aPrice = aViProduct.pricing.summary.fromPrice ?? 0;
            // Rezdy
          } else if (aRezdyProduct.productCode) {
            let amount;
            if (aRezdyProduct.advertisedPrice) {
              amount = aRezdyProduct.advertisedPrice;
            } else {
              amount = aRezdyProduct.priceOptions?.[0].price;
            }
            aPrice = amount ?? 0;
            // Toristy
          } else if (a.provider === Model.PROVIDER_ID.TORISTY) {
            aPrice = Number(aToristyProduct.starting_price.price);
          }

          let bPrice = 0;
          const bGygTour = b as Providers.Gyg.Tour;
          const bBbProduct = b as Providers.Bb.Product;
          const bViProduct = b as Providers.Viator.Product;
          const bRezdyProduct = b as Providers.Rezdy.Product;
          const bToristyProduct = b as unknown as Providers.Toristy.Product;

          // Gyg
          if (bGygTour.tour_id) {
            bPrice = bGygTour.price ? bGygTour.price.values.amount : 0;
            // Bb
          } else if (bBbProduct.info) {
            let amount = bBbProduct.offers[0].priceBreakDowns.find(
              pb => pb.touristType === "ADULT"
            )?.price.amount;
            if (amount === undefined) {
              amount = bBbProduct.offers[0].priceBreakDowns[0].price.amount;
            }
            bPrice = amount ?? 0;
            // Viator
          } else if (bViProduct.translationInfo) {
            bPrice = bViProduct.pricing.summary.fromPrice ?? 0;
            // Rezdy
          } else if (bRezdyProduct.productCode) {
            let amount;
            if (bRezdyProduct.advertisedPrice) {
              amount = bRezdyProduct.advertisedPrice;
            } else {
              amount = bRezdyProduct.priceOptions?.[0].price;
            }
            bPrice = amount ?? 0;
            // Toristy
          } else if (b.provider === Model.PROVIDER_ID.TORISTY) {
            bPrice = Number(bToristyProduct.starting_price.price);
          }

          return aPrice - bPrice;
        });
      });

      return newCatalogs;
    });
  }, [
    gygToursCatalog,
    bbToursCatalog,
    viatorToursCatalog,
    toristyToursCatalog,
    rezdyProductCatalogGroup,
    victoryEventsCatalog,
    occasionGeniusCatalog,
    livnToursCatalog,
    priorityProducts,
  ]);

  // Build priority catalog from priorityProducts
  const priorityCatalog: BookableItem[] = useMemo(() => {
    const items: BookableItem[] = [];

    if (priorityProducts?.viator) {
      priorityProducts.viator.forEach(product => {
        items.push({ ...product, provider: Model.PROVIDER_ID.VIATOR });
      });
    }

    if (priorityProducts?.gyg) {
      priorityProducts.gyg.forEach(tour => {
        items.push({ ...tour, provider: Model.PROVIDER_ID.GYG });
      });
    }

    if (priorityProducts?.toristy) {
      priorityProducts.toristy.forEach(product => {
        items.push({ ...product, provider: Model.PROVIDER_ID.TORISTY });
      });
    }

    if (priorityProducts?.victory) {
      priorityProducts.victory.forEach(event => {
        items.push({ ...event, provider: Model.PROVIDER_ID.VICTORY });
      });
    }

    if (priorityProducts?.bookbarbados) {
      priorityProducts.bookbarbados.forEach(product => {
        items.push({ ...product, provider: Model.PROVIDER_ID.BOOK_BARBADOS });
      });
    }

    if (priorityProducts?.rezdy) {
      priorityProducts.rezdy.forEach(product => {
        items.push({ ...product, provider: Model.PROVIDER_ID.REZDY });
      });
    }

    return items;
  }, [priorityProducts]);

  return {
    finalCatalogs: catalogs,
    priorityCatalog,
    isLoading,
  };
};
